// Favorites — list of restaurants / tastings / places favorited within this trip.
import { Link } from "react-router-dom";
import { ChevronLeft, Heart, Navigation } from "lucide-react";
import { motion } from "framer-motion";
import { useTrip } from "../auth/TripContext";
import { useTripBase } from "../hooks/useTripBase";
import { TRIP as STATIC_TRIP } from "../data/trip";

function findRestaurant(trip, ref) {
  for (const c of Object.keys(trip.restaurants || {})) {
    const found = (trip.restaurants[c] || []).find((r) => r.id === ref);
    if (found) return { ...found, _city: c };
  }
  return null;
}

function findTasting(trip, ref) {
  for (const c of Object.keys(trip.tastings || {})) {
    const found = (trip.tastings[c] || []).find((t) => t.id === ref);
    if (found) return { ...found, _city: c };
  }
  return null;
}

export default function Favorites() {
  const base = useTripBase();
  const { trip, favorites } = useTrip();
  const T = trip || {};

  const buckets = { restaurant: [], tasting: [], place: [] };
  favorites.forEach((f) => {
    if (f.kind === "restaurant") {
      const r = findRestaurant(T, f.ref);
      if (r) buckets.restaurant.push(r);
    } else if (f.kind === "tasting") {
      const t = findTasting(T, f.ref);
      if (t) buckets.tasting.push(t);
    } else if (f.kind === "place") {
      const p = T.places?.[f.ref];
      if (p) buckets.place.push(p);
    }
  });

  const openMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`, "_blank");
  };

  const total = buckets.restaurant.length + buckets.tasting.length + buckets.place.length;

  return (
    <div className="container-app pt-6 pb-28" data-testid="favorites-page">
      <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="favorites-back">
        <ChevronLeft size={16} /> Volver
      </Link>
      <h1 className="text-3xl font-bold leading-tight flex items-center gap-2">
        <Heart size={26} className="fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" /> Favoritos
      </h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">{total} guardado{total === 1 ? "" : "s"}</p>

      {total === 0 && (
        <p className="text-center text-sm text-[hsl(var(--muted))] py-12">
          Aún no tienes favoritos. Toca el corazón ❤️ en cualquier restaurante, sabor o lugar para guardarlo aquí.
        </p>
      )}

      {/* Restaurantes */}
      {buckets.restaurant.length > 0 && (
        <section className="mb-6">
          <h3 className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-2">Restaurantes</h3>
          <ul className="space-y-2">
            {buckets.restaurant.map((r, i) => (
              <motion.li key={r.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }} className="card-paper p-3 flex items-center gap-3" data-testid={`fav-restaurant-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm leading-tight truncate">{r.name}</p>
                  <p className="text-[11px] text-[hsl(var(--muted))]">{r._city} · {r.cuisine}</p>
                </div>
                <button onClick={() => openMaps(r.lat, r.lng)} className="p-2 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" aria-label="Ir">
                  <Navigation size={14} />
                </button>
              </motion.li>
            ))}
          </ul>
        </section>
      )}

      {/* Sabores */}
      {buckets.tasting.length > 0 && (
        <section className="mb-6">
          <h3 className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-2">Sabores</h3>
          <ul className="space-y-2">
            {buckets.tasting.map((t, i) => (
              <motion.li key={t.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }} className="card-paper p-3 flex items-center gap-3" data-testid={`fav-tasting-${t.id}`}>
                <span className="text-2xl">{t.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm leading-tight truncate">{t.name}</p>
                  <p className="text-[11px] text-[hsl(var(--muted))]">{t._city}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </section>
      )}

      {/* Lugares */}
      {buckets.place.length > 0 && (
        <section>
          <h3 className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-2">Lugares</h3>
          <ul className="space-y-2">
            {buckets.place.map((p, i) => (
              <motion.li key={p.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }} className="card-paper p-0 overflow-hidden" data-testid={`fav-place-${p.id}`}>
                <Link to={`${base}/lugar/${p.id}`} className="p-3 flex items-center gap-3 active:scale-[0.99]">
                  <span className="text-2xl">{p.icon || "📍"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm leading-tight truncate">{p.name}</p>
                    <p className="text-[11px] text-[hsl(var(--muted))]">{p.city}</p>
                  </div>
                </Link>
              </motion.li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
